<template>
  <div class="cart-view py-4">
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start mb-4 gap-3">
      <div>
        <h1 class="mb-1">Mon panier</h1>
        <p class="text-muted mb-0">Vérifiez les catalogues sélectionnés avant de valider votre commande.</p>
      </div>
      <div class="text-end">
        <button class="btn btn-outline-primary me-2 mb-2" @click="continueShopping">
          <i class="bi bi-arrow-left-circle me-1"></i> Continuer mes achats
        </button>
        <button class="btn btn-success mb-2" :disabled="!hasItems" @click="goToStep(2)">
          <i class="bi bi-check2-circle me-1"></i> Commencer la validation
        </button>
      </div>
    </div>

    <div class="mb-4">
      <div class="btn-group stepper w-100" role="group">
        <button type="button" class="btn" :class="step === 1 ? 'btn-primary' : 'btn-outline-secondary'"
          @click="goToStep(1)">
          1. Revue du panier
        </button>
        <button type="button" class="btn" :class="step === 2 ? 'btn-primary' : 'btn-outline-secondary'"
          @click="goToStep(2)" :disabled="!hasItems">
          2. Infos client
        </button>
        <button type="button" class="btn" :class="step === 3 ? 'btn-primary' : 'btn-outline-secondary'"
          @click="goToStep(3)" :disabled="!client">
          3. Résumé & confirmation
        </button>
      </div>
    </div>

    <div class="row g-4">
      <div class="col-lg-8">
        <div v-if="step === 1">
          <div v-if="!hasItems" class="card border-0 shadow-sm p-4 text-center">
            <h4 class="mb-3">Votre panier est vide</h4>
            <p class="text-muted mb-3">Ajoutez des catalogues depuis la boutique pour voir votre sélection ici.</p>
            <button class="btn btn-primary" @click="continueShopping">Voir les catalogues</button>
          </div>

          <div v-else>
            <div class="d-none d-md-block table-responsive rounded shadow-sm overflow-hidden">
              <table class="table table-hover mb-0 cart-table">
                <thead class="table-purple text-white">
                  <tr>
                    <th>#</th>
                    <th>Catalogue</th>
                    <th>Code</th>
                    <th>Taille</th>
                    <th>Quantité</th>
                    <th class="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in cartStore.cart" :key="item.id">
                    <td>{{ index + 1 }}</td>
                    <td>
                      <div class="d-flex align-items-center gap-3">
                        <img v-if="item.image_path" :src="$api.defaults.assetsBaseURL + item.image_path" alt="Catalogue"
                          class="cart-item-image rounded" />
                        <div>
                          <div class="fw-bold">{{ item.label || 'Catalogue' }}</div>
                          <div class="text-muted small">{{ item.description ? item.description.substring(0, 70) +
                            (item.description.length > 70 ? '...' : '') : 'Pas de description' }}</div>
                        </div>
                      </div>
                    </td>
                    <td>{{ item.code || '—' }}</td>
                    <td>
                      <select class="form-select form-select-sm" v-model="item.style_body_size" @change="saveCart">
                        <option value="S">S</option>
                        <option value="M">M</option>
                        <option value="L">L</option>
                        <option value="XL">XL</option>
                        <option value="XXL">XXL</option>
                      </select>
                    </td>
                    <td>
                      <div class="d-flex align-items-center gap-2">
                        <button class="btn btn-sm btn-outline-secondary" @click="updateQuantity(item, -1)">-</button>
                        <span class="badge bg-secondary">{{ item.quantity || 1 }}</span>
                        <button class="btn btn-sm btn-outline-secondary" @click="updateQuantity(item, 1)">+</button>
                      </div>
                    </td>
                    <td class="text-end">
                      <button class="btn btn-sm btn-danger" @click="removeItem(item)">
                        <i class="bi bi-trash"></i> Retirer
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="d-block d-md-none">
              <div class="card mb-3" v-for="item in cartStore.cart" :key="item.id">
                <div class="card-body">
                  <div class="d-flex align-items-start gap-3 mb-3">
                    <img v-if="item.image_path" :src="$api.defaults.assetsBaseURL + item.image_path" alt="Catalogue"
                      class="cart-item-image rounded" />
                    <div class="flex-grow-1">
                      <h5 class="mb-1">{{ item.label || 'Catalogue' }}</h5>
                      <p class="text-muted mb-1">Code : {{ item.code || '—' }}</p>
                      <p class="text-muted small mb-2">{{ item.description ? item.description.substring(0, 100) +
                        (item.description.length > 100 ? '...' : '') : 'Pas de description' }}</p>
                      <div class="mb-2">
                        <label class="form-label small mb-1">Taille</label>
                        <select class="form-select form-select-sm" v-model="item.style_body_size" @change="saveCart">
                          <option value="S">S</option>
                          <option value="M">M</option>
                          <option value="L">L</option>
                          <option value="XL">XL</option>
                          <option value="XXL">XXL</option>
                        </select>
                      </div>
                      <div class="d-flex align-items-center gap-2 mb-3">
                        <button class="btn btn-sm btn-outline-secondary" @click="updateQuantity(item, -1)">-</button>
                        <span class="badge bg-secondary">{{ item.quantity || 1 }}</span>
                        <button class="btn btn-sm btn-outline-secondary" @click="updateQuantity(item, 1)">+</button>
                      </div>
                      <button class="btn btn-sm btn-danger" @click="removeItem(item)">Retirer</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="step === 2">
          <div class="card border-0 shadow-sm p-4">
            <h4 class="mb-3">Étape 2 : Enregistrer le client</h4>
            <ClientFromOrderForm ref="clientOrderForm" :tenant_id="tenant_id" :catalog_id="firstCatalogId"
              @client-info-saved="onClientSaved" @client-info-removed="onClientremoved" />
            <div class="d-flex justify-content-between mt-4">
              <button class="btn btn-outline-secondary" @click="goToStep(1)"> <i
                  class="bi bi-arrow-left"></i>Retour</button>
              <button class="btn btn-primary d-none" @click="goToStep(3)">Aller au résumé</button>
            </div>
            <div v-if="clientError" class="alert alert-danger mt-3">{{ clientError }}</div>
          </div>
        </div>

        <div v-else-if="step === 3">
          <div class="card border-0 shadow-sm p-4">
            <h4 class="mb-3">Étape 3 : Résumé de la commande</h4>
            <div class="mb-4">
              <h5 class="mb-2">Client</h5>
              <div v-if="client">
                <p class="mb-1"><strong>{{ client.nom }}</strong> {{ client.prenoms }}</p>
                <p class="mb-1">Téléphone : {{ client.telephone }}</p>
                <p class="mb-1" v-if="client.adresse">Adresse : {{ client.adresse }}</p>
                <p class="mb-1">Genre : {{ client.genre }}</p>
              </div>
              <div v-else class="text-muted">Aucun client sauvegardé pour le moment.</div>
            </div>

            <div class="mb-4">
              <h5 class="mb-2">Produits</h5>
              <ul class="list-group">
                <li class="list-group-item d-flex justify-content-between align-items-start"
                  v-for="(item) in cartStore.cart" :key="item.id">
                  <div>
                    <div class="fw-bold">{{ item.label || 'Catalogue' }}</div>
                    <small class="text-muted">{{ item.code || '—' }} - Taille {{ item.style_body_size || 'M' }}</small>
                  </div>
                  <span class="badge bg-secondary rounded-pill">{{ item.quantity || 1 }}</span>
                </li>
              </ul>
            </div>

            <div class="d-flex justify-content-between align-items-center">
              <button class="btn btn-outline-secondary" @click="goToStep(2)"> <i
                  class="bi bi-arrow-left"></i>Retour</button>
              <button class="btn btn-success d-none" :disabled="!client || !hasItems" @click="confirmOrder">Confirmer la
                commande</button>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-4">
        <div class="card border-0 shadow-sm summary-card p-4">
          <h5 class="mb-3">Résumé</h5>
          <div class="d-flex justify-content-between mb-2 d-none">
            <span>Étape actuelle</span>
            <strong>{{ stepLabel }}</strong>
          </div>
          <div class="border-top my-3"></div>
          <div class="d-flex justify-content-between mb-2">
            <span>Articles</span>
            <strong>{{ totalCatalogues }}</strong>
          </div>
          <div class="d-flex justify-content-between mb-2">
            <span>Quantité totale</span>
            <strong>{{ totalItems }}</strong>
          </div>
          <div class="d-flex justify-content-between mb-2">
            <span>Tailles sélectionnées</span>
            <strong>{{ sizeSummary }}</strong>
          </div>
          <div v-if="client" class="border-top my-3 pt-3">
            <div class="mb-2"><strong>Client enregistré</strong></div>
            <div class="small text-muted">{{ client.nom }} {{ client.prenoms }}<br />{{ client.telephone }}</div>
          </div>
          <div class="border-top my-3"></div>
          <button v-if="step === 1" class="btn btn-success w-100 mb-2" :disabled="!hasItems"
            @click="goToStep(2)">Suivant</button>
          <button v-else-if="step === 2" class="btn btn-success w-100 mb-2" :disabled="!client"
            @click="goToStep(3)">Aller au résumé</button>
          <button v-else-if="step === 3" class="btn btn-success w-100 mb-2" :disabled="!client"
            @click="confirmOrder">Confirmer</button>
          <button v-if="step == 1" class="btn btn-outline-danger w-100" :disabled="!hasItems" @click="clearCart">
            <i class="bi bi-trash me-1"></i> Vider le panier
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import { cartStore } from '@/store/cartStore';
import ClientFromOrderForm from '@/components/forms/ClientFromOrderForm.vue';

export default {
  name: 'CartView',
  components: {
    ClientFromOrderForm,
  },
  props: {
    tenant_id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      step: 1,
      client: null,
      clientError: '',
      saveInfo: 0,
    };
  },
  computed: {
    cartStore() {
      return cartStore();
    },
    hasItems() {
      return this.cartStore.cart.length > 0;
    },
    totalItems() {
      return this.cartStore.cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
    },
    totalCatalogues() {
      return this.cartStore.cart.length;
    },
    sizeSummary() {
      const sizes = this.cartStore.cart.reduce((acc, item) => {
        const size = item.style_body_size || 'M';
        acc[size] = (acc[size] || 0) + 1;
        return acc;
      }, {});
      return Object.entries(sizes)
        .map(([size, count]) => `${size}(${count})`)
        .join(', ');
    },
    stepLabel() {
      if (this.step === 1) return 'Revue du panier';
      if (this.step === 2) return 'Infos client';
      return 'Résumé';
    },
    firstCatalogId() {
      return this.cartStore.cart.length ? this.cartStore.cart[0].id : '';
    },
  },
  watch: {
    'cartStore.customerInfo'() {
      this.loadClientInfo();
    },
  },
  methods: {
    saveCart() {
      this.cartStore.save();
    },
    updateQuantity(item, delta) {
      const index = this.cartStore.cart.findIndex((entry) => entry.id === item.id);
      if (index === -1) return;
      const nextQuantity = (this.cartStore.cart[index].quantity || 1) + delta;
      if (nextQuantity <= 0) {
        this.cartStore.cart.splice(index, 1);
      } else {
        this.cartStore.cart[index].quantity = nextQuantity;
      }
      this.cartStore.save();
    },
    removeItem(item) {
      this.cartStore.cart = this.cartStore.cart.filter((entry) => entry.id !== item.id);
      this.cartStore.save();
    },
    clearCart() {
      Swal.fire({
        title: 'Vider le panier',
        text: 'Êtes-vous sûr de vouloir supprimer tous les catalogues du panier ?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Oui, vider',
        cancelButtonText: 'Annuler',
      }).then((result) => {
        if (result.isConfirmed) {
          this.cartStore.removeCart();
          Swal.fire('Panier vidé', 'Votre panier a été vidé.', 'success');
          this.step = 1;
          this.client = null;
          this.clientError = '';
        }
      });
    },
    goToStep(step) {
      if (step === 2 && !this.hasItems) return;
      if (step === 3 && !this.client) return;
      this.step = step;
    },
    // saveCustumerInfoStep() {
    //   if (!this.$refs.clientOrderForm.isValid) {
    //     Swal.fire('Formulaire incomplet', 'Veuillez remplir tous les champs obligatoires du formulaire client avant de continuer.', 'error');
    //     return;
    //   }
    //   this.saveInfo++;
    //   this.goToStep(3);
    // },
    onClientSaved(savedClientInfo) {
      this.client = savedClientInfo;
      this.clientError = '';
      this.step = 3;
    },
    onClientremoved() {
      this.client = null;
      this.clientError = '';
    },
    onClientError(error) {
      this.clientError = error.response?.data?.message || 'Erreur lors de la sauvegarde du client.';
    },
    // confirmOrder() {
    //   if (!this.hasItems || !this.client) {
    //     return;
    //   }

    //   const itemsList = this.cartStore.cart
    //     .map((item) => `- ${item.label || 'Catalogue'} x${item.quantity || 1} (${item.style_body_size || 'M'})`)
    //     .join('\n');

    //   Swal.fire({
    //     title: 'Confirmer la commande',
    //     icon: 'question',
    //     html: `<p>Client : <strong>${this.client.nom} ${this.client.prenoms || ''}</strong></p><pre class="text-start" style="white-space: pre-wrap; text-align: left;">${itemsList}</pre>`,
    //     confirmButtonText: 'Confirmer',
    //     cancelButtonText: 'Retour',
    //     showCancelButton: true,
    //     width: 600,
    //   }).then((result) => {
    //     if (result.isConfirmed) {
    //       Swal.fire('Commande confirmée', 'Votre commande a bien été enregistrée.', 'success');
    //       this.step = 1;
    //       this.client = null;
    //       this.cartStore.removeCart();
    //     }
    //   });
    // },
    confirmOrder() {
      let orderData = {
        user_first_name: this.client.nom,
        user_last_name: this.client.prenoms || '',
        user_phone: this.client.telephone,
        user_address: this.client.adresse || '',
        user_genre: this.client.genre,
        styles: this.cartStore.cart.map(item => ({
          style_code: item.code,
          style_quantity: item.quantity || 1,
          style_body_size: item.style_body_size || 'M',
        })),
      };

      console.log('Données de la commande à confirmer :', orderData);

      this.$api.post(`/catalogs_for_clients/${this.tenant_id}/place_order`, orderData)
        .then(response => {
          Swal.fire('Commande créée', response.data.message, 'success').then(()=>{
            this.continueShopping();
          });
          this.step = 1;
          this.client = null;
          this.cartStore.removeCart();
        })
        .catch(error => {
          Swal.fire('Erreur', error.response.data.message, 'error');
        });
    },
    continueShopping() {
      if (this.tenant_id) {
        this.$router.push({ name: 'catalogues_for_clients', params: { tenant_id: this.tenant_id } });
      } else {
        this.$router.push({ name: 'home' });
      }
    },
    loadClientInfo() {
      if (this.cartStore.customerInfo && Object.keys(this.cartStore.customerInfo).length > 0) {
        this.client = this.cartStore.customerInfo;
      } else {
        this.client = null;
      }
    }
  },
  mounted() {
    this.cartStore.load();
    this.loadClientInfo();
  },
};
</script>

<style scoped>
.cart-view {
  min-height: 100%;
}

.cart-table {
  border: 1px solid rgba(106, 74, 166, 0.18);
  background: #ffffff;
}

.table-purple {
  background-color: #6a4aa6;
}

.cart-item-image {
  width: 80px;
  height: 80px;
  object-fit: cover;
}

.stepper .btn {
  flex: 1;
  min-width: 150px;
}

.summary-card {
  min-height: 320px;
}
</style>